import { FC, MouseEvent, useRef, useEffect, useCallback, useState } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { transparentize } from 'polished';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';

const containerStyle = (theme: Theme) => css`
  position: relative;
  width: 100%;
  height: 120px;

  > div {
    padding-top: 169.88%;
  }

  > svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: visible;

    .tick,
    .domain {
      color: ${theme.color.grey[60]};
    }

    text {
      color: ${theme.color.grey[80]};
    }
  }
`;

export type LineGraphData = { timestamp: Date; value: number }[];

interface LineGraphPoint {
  timestamp: Date;
  value: number;
  element: SVGCircleElement;
}

const loadGraph = async (theme: Theme, svgElement: Element, data: LineGraphData, scale: [number, number] | null = null) => {
  const d3 = await import('d3');

  const { width: svgWidth, height: svgHeight } = svgElement.getBoundingClientRect();

  var margin = { top: 10, right: 0, bottom: 30, left: 22 },
    width = svgWidth - margin.left - margin.right,
    height = svgHeight - margin.top - margin.bottom;

  svgElement.innerHTML = '';

  const svg = d3
    .select(svgElement)
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

  const values = data.map(({ value }) => (scale ? Math.min(scale[1], Math.max(scale[0], value)) : value));
  const min = Math.min(...values);
  const max = Math.max(...values);

  const x = d3
    .scaleTime()
    .domain(d3.extent(data, (point) => point.timestamp) as [Date, Date])
    .range([0, width]);

  svg
    .append('g')
    .attr('transform', 'translate(0,' + height + ')')
    .call(d3.axisBottom(x).ticks(values.length));

  // Add Y axis
  const y = d3
    .scaleLinear()
    .domain(scale || [min, max])
    .range([height, 0]);

  svg
    .append('g')
    .call(d3.axisLeft(y).ticks(3))
    .call((g) => g.select('.domain').remove());

  // Grid lines
  svg
    .append('g')
    .call(
      d3
        .axisLeft(y)
        .ticks(3)
        .tickSize(-width)
        .tickFormat(() => '')
    )
    .call((g) => g.select('.domain').remove());

  // Add the line
  svg
    .append('path')
    .datum(data.map(({ timestamp, value }) => [timestamp, value]))
    .attr('fill', 'none')
    .attr('stroke', theme.color.primary[100])
    .attr('stroke-width', 2)
    .attr(
      'd',
      d3
        .line()
        .x((d) => x(d[0]))
        .y((d) => y(d[1])) as any
    );

  // Add the area
  svg
    .append('path')
    .datum(data.map(({ timestamp, value }) => [timestamp, value]))
    .attr('fill', transparentize(0.88, theme.color.primary[100]))
    .attr(
      'd',
      d3
        .area()
        .x((d) => x(d[0]))
        .y0(y(0))
        .y1((d) => y(d[1])) as any
    );

  const lineGraphPoints: LineGraphPoint[] = [];

  // Add the points
  const points = svg
    .append('g')
    .selectAll('dot')
    .data(data)
    .enter()
    .append('circle')
    .attr('cx', (d) => x(d.timestamp))
    .attr('cy', (d) => y(d.value))
    .attr('r', 4)
    .attr('fill', theme.color.primary[100]);

  points.each(({ timestamp, value }, index, elements) => lineGraphPoints.push({ timestamp, value, element: elements[index] }));

  return lineGraphPoints;
};

export interface LineGraphProps {
  data: { timestamp: Date; value: number }[];
  scale?: [number, number] | null;
}

export const LineGraph: FC<LineGraphProps> = ({ data, scale }) => {
  const graphRef = useRef<SVGSVGElement>(null);
  const theme = useTheme();
  const [points, setPoints] = useState<LineGraphPoint[]>([]);
  const [tooltipProps, setTooltipProps] = useState<Partial<TooltipProps> | null>(null);

  useEffect(() => {
    if (!graphRef.current || !data) return;
    loadGraph(theme, graphRef.current, data, scale).then(setPoints).catch(console.error);
  }, [graphRef.current, data, scale]);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const rects = points.map(({ element }) => element.getBoundingClientRect());
      const relative = rects.map(({ x, width }) => Math.abs(x + width / 2 - event.clientX));
      const closestIndex = relative.indexOf(Math.min(...relative));

      if (!graphRef.current || !rects[closestIndex]) return;

      const { y, height } = graphRef.current.getBoundingClientRect();
      const placement = (y || 0) + height / 3 - rects[closestIndex].y <= 0 ? 'top' : 'bottom';

      setTooltipProps({
        title: Math.round(points[closestIndex].value) + ' %',
        placement,
        PopperProps: { anchorEl: points[closestIndex].element },
      });
    },
    [points]
  );

  return (
    <div css={containerStyle}>
      <Tooltip title="Loading…" arrow {...tooltipProps}>
        <svg ref={graphRef} onMouseMoveCapture={handleMouseMove} />
      </Tooltip>
    </div>
  );
};
